import { lazy, type LazyExoticComponent, type ComponentType } from 'react';
import type { IconName } from '@/shared/components/ui/Icon';
import type { MessageKey } from '@/shared/i18n/messages';

export type ModuleId =
  | 'folders'
  | 'numadmin'
  | 'admin'
  | 'calendar'
  | 'trips'
  | 'sheets'
  | 'notes'
  | 'cashflow'
  | 'investments'
  | 'stock'
  | 'payroll'
  | 'calculators'
  | 'forms';

/** Minimum plan required. Enforced later by the `user_has_module` RPC — never
 *  hardcode plan checks in the frontend beyond this label. */
export type ModulePlan = 'public' | 'free' | 'basic' | 'pro' | 'business' | 'corporate';

export interface ModuleEntry {
  readonly id: ModuleId;
  /** Route path under AppShell: numtable.com/<path>. */
  readonly path: string;
  readonly icon: IconName;
  readonly plan: ModulePlan;
  readonly nameKey: MessageKey;
  readonly descriptionKey: MessageKey;
  readonly Component: LazyExoticComponent<ComponentType>;
  /**
   * Only shown while the platform organization is active. Presentation only —
   * the module itself renders a 404 outside it, and every request it makes is
   * re-authorized by the Worker with `service_role`.
   */
  readonly platformOnly?: boolean;
  /** Routed but left out of the apps menu, Home shortcuts and search. */
  readonly unlisted?: boolean;
}

/** The menu for one organization. */
export function modulesFor(isPlatformOrg: boolean): readonly ModuleEntry[] {
  return MODULES.filter((module) => !module.platformOnly || isPlatformOrg);
}

/** What the apps menu, Home shortcuts and module search show. */
export function listedModulesFor(isPlatformOrg: boolean): readonly ModuleEntry[] {
  return modulesFor(isPlatformOrg).filter((module) => !module.unlisted);
}

/**
 * The single registry the menu and the router both read.
 * Every module is lazy-loaded — the shell must not pull them into the
 * initial bundle.
 */
export const MODULES: readonly ModuleEntry[] = [
  {
    id: 'admin',
    path: 'admin',
    icon: 'user',
    plan: 'free',
    nameKey: 'modules.admin.name',
    descriptionKey: 'modules.admin.description',
    Component: lazy(() => import('@/modules/admin')),
  },
  // Unlisted: Home's My files tab replaced it. The route stays so old
  // /folders links redirect there.
  {
    id: 'folders',
    path: 'folders',
    unlisted: true,
    icon: 'folder',
    plan: 'free',
    nameKey: 'modules.folders.name',
    descriptionKey: 'modules.folders.description',
    Component: lazy(() => import('@/modules/folders')),
  },
  {
    id: 'calendar',
    path: 'calendar',
    icon: 'calendar',
    plan: 'pro',
    nameKey: 'modules.calendar.name',
    descriptionKey: 'modules.calendar.description',
    Component: lazy(() => import('@/modules/calendar')),
  },
  {
    id: 'trips',
    path: 'trips',
    icon: 'plane',
    plan: 'free',
    nameKey: 'modules.trips.name',
    descriptionKey: 'modules.trips.description',
    Component: lazy(() => import('@/modules/trips')),
  },
  {
    id: 'sheets',
    path: 'sheets',
    icon: 'sheets',
    plan: 'free',
    nameKey: 'modules.sheets.name',
    descriptionKey: 'modules.sheets.description',
    Component: lazy(() => import('@/modules/sheets')),
  },
  {
    id: 'notes',
    path: 'notes',
    icon: 'notes',
    plan: 'free',
    nameKey: 'modules.notes.name',
    descriptionKey: 'modules.notes.description',
    Component: lazy(() => import('@/modules/notes')),
  },
  {
    id: 'cashflow',
    path: 'cashflow',
    icon: 'cashflow',
    plan: 'free',
    nameKey: 'modules.cashflow.name',
    descriptionKey: 'modules.cashflow.description',
    Component: lazy(() => import('@/modules/cashflow')),
  },
  {
    id: 'investments',
    path: 'investments',
    icon: 'investments',
    plan: 'free',
    nameKey: 'modules.investments.name',
    descriptionKey: 'modules.investments.description',
    Component: lazy(() => import('@/modules/investments')),
  },
  {
    id: 'forms',
    path: 'forms',
    icon: 'sheets',
    plan: 'free',
    nameKey: 'modules.forms.name',
    descriptionKey: 'modules.forms.description',
    Component: lazy(() => import('@/modules/forms')),
  },
  {
    id: 'stock',
    path: 'stock',
    icon: 'stock',
    plan: 'business',
    nameKey: 'modules.stock.name',
    descriptionKey: 'modules.stock.description',
    Component: lazy(() => import('@/modules/stock')),
  },
  {
    id: 'payroll',
    path: 'payroll',
    icon: 'payroll',
    plan: 'business',
    nameKey: 'modules.payroll.name',
    descriptionKey: 'modules.payroll.description',
    Component: lazy(() => import('@/modules/payroll')),
  },
  {
    id: 'calculators',
    path: 'calculators',
    icon: 'calculators',
    plan: 'public',
    nameKey: 'modules.calculators.name',
    descriptionKey: 'modules.calculators.description',
    Component: lazy(() => import('@/modules/calculators')),
  },
  // Last, and only visible from inside the platform organization.
  {
    id: 'numadmin',
    path: 'numadmin',
    icon: 'building',
    plan: 'free',
    nameKey: 'modules.numadmin.name',
    descriptionKey: 'modules.numadmin.description',
    Component: lazy(() => import('@/modules/numadmin')),
    platformOnly: true,
  },
];

export const PLAN_LABEL_KEY: Record<ModulePlan, MessageKey> = {
  public: 'plan.public',
  free: 'plan.free',
  basic: 'plan.basic',
  pro: 'plan.pro',
  business: 'plan.business',
  corporate: 'plan.corporate',
};
